import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Grid, TextField, Typography } from "@mui/material";

// Datos en vivo de la API Georef (Servicio de Normalización de Datos Geográficos de Argentina)
const GEOREF_URL = "https://apis.datos.gob.ar/georef/api";
// Georef no admite más de 5000 resultados por pedido ni paginar más allá de 10000
const PAGE_SIZE = 5000;
const MAX_OFFSET = 10000;
const MAX_RESULTS = 30;

const fetchAllPages = async (endpoint, key, params) => {
  const items = [];
  let total = Infinity;
  for (let inicio = 0; inicio < total && inicio < MAX_OFFSET; inicio += PAGE_SIZE) {
    const query = new URLSearchParams({ ...params, max: PAGE_SIZE, inicio, aplanar: true });
    const res = await fetch(`${GEOREF_URL}/${endpoint}?${query}`);
    if (!res.ok) throw new Error(`Georef respondió ${res.status}`);
    const data = await res.json();
    items.push(...data[key]);
    total = data.total;
  }
  return items;
};

const normalize = (text) =>
  text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

// Georef devuelve muchos nombres en mayúsculas: "AV RIVADAVIA" -> "Av Rivadavia"
const toTitle = (text) =>
  text === text.toUpperCase()
    ? text.toLowerCase().replace(/(^|[\s(.-])(\p{L})/gu, (_, sep, letter) => sep + letter.toUpperCase())
    : text;

const provinceName = (name) => (name.startsWith("Ciudad Autónoma") ? "CABA" : name);

let localitiesPromise = null;
const loadLocalities = () => {
  if (!localitiesPromise) {
    localitiesPromise = fetchAllPages("localidades-censales", "localidades_censales", {
      campos: "id,nombre,provincia.nombre",
    })
      .then((list) =>
        list.map((loc) => {
          const nombre = toTitle(loc.nombre);
          const provincia = provinceName(loc.provincia_nombre);
          return { id: loc.id, nombre, provincia, label: `${nombre}, ${provincia}` };
        })
      )
      .catch((error) => {
        localitiesPromise = null;
        return [];
      });
  }
  return localitiesPromise;
};

const streetsCache = new Map();
const loadStreets = (localityId) => {
  if (!streetsCache.has(localityId)) {
    const promise = fetchAllPages("calles", "calles", { localidad_censal: localityId, campos: "nombre" })
      .then((list) => [...new Set(list.map((street) => toTitle(street.nombre)))].sort((a, b) => a.localeCompare(b, "es")))
      .catch((error) => {
        streetsCache.delete(localityId);
        return [];
      });
    streetsCache.set(localityId, promise);
  }
  return streetsCache.get(localityId);
};

// Primero lo que empieza con el texto buscado, después lo que lo contiene
const rankOptions = (getLabel, showAllWhenEmpty) => (options, { inputValue }) => {
  const query = normalize(inputValue);
  if (!query) return showAllWhenEmpty ? options.slice(0, MAX_RESULTS) : [];
  const starts = [];
  const contains = [];
  for (const option of options) {
    const label = normalize(getLabel(option));
    if (label.startsWith(query)) starts.push(option);
    else if (label.includes(query)) contains.push(option);
    if (starts.length >= MAX_RESULTS) break;
  }
  return [...starts, ...contains].slice(0, MAX_RESULTS);
};

const filterLocalities = rankOptions((loc) => loc.label, false);
const filterStreets = rankOptions((street) => street, true);

// "Rivadavia 500, Luján, Buenos Aires" -> { street, number, locality }
const parseAddress = (value) => {
  const parts = (value || "").split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length < 3) return { street: "", number: "", locality: parts.join(", ") };
  const locality = parts.slice(-2).join(", ");
  const streetPart = parts.slice(0, -2).join(", ");
  const match = streetPart.match(/^(.*?)\s+(\d+\w?)$/);
  return match
    ? { street: match[1], number: match[2], locality }
    : { street: streetPart, number: "", locality };
};

const formatAddress = (street, number, locality) => {
  const streetLine = [street.trim(), number.trim()].filter(Boolean).join(" ");
  return [streetLine, locality.trim()].filter(Boolean).join(", ");
};

// Dirección con autocompletado de localidad y calle (Georef) + altura.
// onChange recibe { target: { name, value, city } }: value es la dirección completa
// ("Calle 123, Localidad, Provincia") y city sólo la localidad ("Localidad, Provincia").
// stacked: usa siempre el diseño angosto (para formularios dentro de tarjetas o diálogos chicos)
const AddressField = ({ name, value, onChange, label, required, size, stacked = false }) => {
  const [localities, setLocalities] = useState([]);
  const [loadingLocalities, setLoadingLocalities] = useState(true);
  const [locality, setLocality] = useState(null);
  const [localityText, setLocalityText] = useState("");
  const [streets, setStreets] = useState([]);
  const [loadingStreets, setLoadingStreets] = useState(false);
  const [street, setStreet] = useState("");
  const [number, setNumber] = useState("");
  const lastEmitted = useRef(null);

  useEffect(() => {
    let active = true;
    loadLocalities().then((list) => {
      if (!active) return;
      setLocalities(list);
      setLoadingLocalities(false);
    });
    return () => {
      active = false;
    };
  }, []);

  // Cargar el valor recibido desde afuera (por ejemplo, datos ya guardados)
  useEffect(() => {
    if (value === lastEmitted.current) return;
    const parsed = parseAddress(value);
    setStreet(parsed.street);
    setNumber(parsed.number);
    setLocalityText(parsed.locality);
    setLocality(null);
  }, [value]);

  // Vincular el texto de la localidad con una localidad conocida para poder sugerir calles
  useEffect(() => {
    if (locality || !localityText || localities.length === 0) return;
    const target = normalize(localityText);
    const match = localities.find((loc) => normalize(loc.label) === target);
    if (match) setLocality(match);
  }, [localities, locality, localityText]);

  useEffect(() => {
    setStreets([]);
    if (!locality) return;
    let active = true;
    setLoadingStreets(true);
    loadStreets(locality.id).then((list) => {
      if (!active) return;
      setStreets(list);
      setLoadingStreets(false);
    });
    return () => {
      active = false;
    };
  }, [locality]);

  const emit = (nextStreet, nextNumber, nextLocality) => {
    const full = formatAddress(nextStreet, nextNumber, nextLocality);
    lastEmitted.current = full;
    onChange({ target: { name, value: full, city: nextLocality.trim() } });
  };

  return (
    <>
      {label && (
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
          {label}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={stacked ? 12 : 5}>
          <Autocomplete
            freeSolo
            options={localities}
            loading={loadingLocalities}
            filterOptions={filterLocalities}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.label}
              </li>
            )}
            value={locality}
            onChange={(_, selected) => {
              const next = selected && typeof selected === "object" ? selected : null;
              setLocality(next);
              setStreet("");
              setNumber("");
              const text = next ? next.label : selected || "";
              setLocalityText(text);
              emit("", "", text);
            }}
            inputValue={localityText}
            onInputChange={(_, text, reason) => {
              if (reason !== "input") return;
              setLocalityText(text);
              if (locality && text !== locality.label) setLocality(null);
              emit(street, number, text);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Localidad"
                placeholder="Ej: Luján"
                required={required}
                size={size}
                autoComplete="off"
              />
            )}
          />
        </Grid>
        <Grid item xs={8} md={stacked ? 8 : 5}>
          <Autocomplete
            freeSolo
            options={streets}
            loading={loadingStreets}
            filterOptions={filterStreets}
            inputValue={street}
            onInputChange={(_, text) => {
              setStreet(text);
              emit(text, number, localityText);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Calle"
                placeholder={locality ? "Buscá la calle" : "Elegí primero la localidad"}
                size={size}
                autoComplete="off"
              />
            )}
          />
        </Grid>
        <Grid item xs={4} md={stacked ? 4 : 2}>
          <TextField
            label="Altura"
            value={number}
            onChange={(event) => {
              const next = event.target.value.replace(/[^\dA-Za-z]/g, "");
              setNumber(next);
              emit(street, next, localityText);
            }}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            size={size}
            fullWidth
            autoComplete="off"
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AddressField;
